/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
// internal deps
import {
    IRemovePlanMessage,
    RenderingDetails,
    RenderingDetailsMeta,
    SavingsLineItemButtonType,
    SavingsLineItemData,
    SavingsRightSectionType,
} from "@swiggy-private/cart-types";
import { priceInRupee } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { CTATypes, isCTATypePresent } from "@swiggy-private/super-ui";
import { useGetPropsForPlanConfirmationBS } from "@swiggy-private/super-ui";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
// helpers
import { getUserSuperStatus, navigateToDeeplinkGenericHelper } from "@cart-client/helpers/Cart";
// hooks
import { useSuperAnalytics } from "./useSuperAnalytics";
import { useOneMembershipPlanHandlers } from "./useOneMembershipPlanHandlers";
import { useTheme } from "styled-components";
// helpers
import {
    getSubtitleAndButtonData,
    getSuperWidgetData,
    pickSubtitles,
    superOnLoad,
} from "@cart-client/helpers/superCartTwoDotO";
import { isSwiggyBlackPlanVariants } from "@cart-client/helpers/SwiggyPlanBullsEyeTg";

// constants
import { APPLIED } from "@cart-client/constants/index";

// analytics
import { clickOneTapAddPlan } from "@cart-client/analytics/events";
// nr events
import {
    SWIGGY_ONE_COMPONENT,
    SWIGGY_ONE_STATE,
    recordSwiggyOneNREvent,
    recordSwiggyOneNREventDataTestIdVisibility,
} from "@swiggy-private/super-ui";
interface ReturnType {
    getSubscriptionSavingsLineItems: () => SavingsLineItemData[];
    removePlanMessage: IRemovePlanMessage;
}

const useSubscriptionSavings = (subscriptionSavingsRenderingDetails: RenderingDetails[]): ReturnType => {
    useEffect(() => {
        if (isEmpty(subscriptionSavingsRenderingDetails)) {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.SAVINGS_CORNER, SWIGGY_ONE_STATE.NO_DATA);
        } else {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.SAVINGS_CORNER, SWIGGY_ONE_STATE.MOUNTED, {
                itemCount: String(subscriptionSavingsRenderingDetails.length),
            });
        }
    }, [subscriptionSavingsRenderingDetails]);

    const [showRemovePlanConfirmation, setShowRemovePlanConfirmation] = useState(false);
    const { clickEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const userSuperStatus = useMemo(() => getUserSuperStatus(cartResponse) || "", [cartResponse]);
    const { addSuperToCart, removeSuperFromCart } = useOneMembershipPlanHandlers();

    const {
        onImpressionSuperOneTapAddNudge,
        onImpressionSuperViewAllBenefitsCTA,
        onImpressionSwiggyOneSavingsNudge,
        onImpressionSuperRemovePlanConfirmationHalfCard,
        onImpressionSuperRemovePlanHalfCardRemoveButton,
        onImpressionSuperRemovePlanHalfCardBackButton,
        onClickSuperRemovePlanHalfCardBackButton,
        onClickSuperRemovePlanHalfCardRemoveButton,
        onClickSuperViewAllBenefitsCTA,
        onClickRemoveSuper,
    } = useSuperAnalytics(userSuperStatus);
    const { isNudgeUi, isBenefitsOneTapUi, isActiveUserUi, widgetData } = getSuperWidgetData(
        subscriptionSavingsRenderingDetails,
    );
    const { bottomSheetProps, removePlanMessage } = useGetPropsForPlanConfirmationBS(
        widgetData || {},
        showRemovePlanConfirmation,
        {
            setShowRemovePlanConfirmation,
            removeSuper: removeSuperFromCart,
            impressionSuperRemovePlanConfirmationHalfCard: onImpressionSuperRemovePlanConfirmationHalfCard,
            impressionSuperRemovePlanHalfCardRemoveButton: onImpressionSuperRemovePlanHalfCardRemoveButton,
            impressionSuperRemovePlanHalfCardBackButton: onImpressionSuperRemovePlanHalfCardBackButton,
            onClickSuperRemovePlanHalfCardBackButton,
            onClickSuperRemovePlanHalfCardRemoveButton,
        },
    );
    const allowPlanRemovalBottomsheet = useMemo(
        () =>
            showRemovePlanConfirmation &&
            !isEmpty(bottomSheetProps?.title) &&
            !isEmpty(bottomSheetProps?.content[0]?.title),
        [bottomSheetProps?.content, bottomSheetProps?.title, showRemovePlanConfirmation],
    );
    const handleRemove = useCallback(
        (meta: RenderingDetailsMeta) => {
            if (!isEmpty(meta?.remove_plan_message)) {
                onClickRemoveSuper(
                    meta?.plan_details?.plan_id,
                    meta?.plan_details?.name,
                    priceInRupee(meta?.plan_details?.final_price),
                );
                setShowRemovePlanConfirmation(true);
            } else {
                removeSuperFromCart();
            }
        },
        [onClickRemoveSuper, removeSuperFromCart],
    );

    const theme = useTheme();

    const getSubscriptionSavingsLineItems = useCallback(() => {
        const subscriptionLineItems: SavingsLineItemData[] = [];

        if (isNudgeUi || isBenefitsOneTapUi) {
            const { plan_details, secondary_subtitles, logo_ctx, identifiers } = widgetData?.meta || {};
            const { subtitle, oneTapButton } = getSubtitleAndButtonData(widgetData?.meta || {});
            const isAddPlan = isCTATypePresent(widgetData?.meta?.ctas, CTATypes.ONE_TAP_ADD);
            const title = isNudgeUi ? widgetData?.display_text : widgetData?.meta?.benefits?.[0]?.title;
            const bottomText = pickSubtitles(secondary_subtitles, identifiers);

            recordSwiggyOneNREventDataTestIdVisibility(
                SWIGGY_ONE_COMPONENT.SAVINGS_CORNER,
                "",
                `[data-buttontext*="${oneTapButton?.text}"]`,
            );

            const addSuperCallback = () => {
                clickOneTapAddPlan(
                    {
                        title,
                        ctaText: oneTapButton?.text || "",
                        subtitle: subtitle?.text || "",
                        userSuperStatus,
                        planId: plan_details?.plan_id,
                        planPrice: priceInRupee(plan_details?.final_price),
                        planName: plan_details?.name,
                    },
                    clickEvent,
                );
                addSuperToCart(String(plan_details?.plan_id), undefined, plan_details?.type);
            };
            const onLoadCallback = () => {
                superOnLoad({
                    isAddPlan,
                    title,
                    ctaText: oneTapButton?.text,
                    planId: plan_details?.plan_id,
                    planName: plan_details?.name,
                    planPrice: plan_details?.final_price,
                    subtitle: subtitle,
                    onImpressionSuperOneTapAddNudge,
                    onImpressionSuperViewAllBenefitsCTA,
                    onImpressionSwiggyOneSavingsNudge,
                    totalSavings: bottomText,
                });
            };
            const handleSubtitleClick = () => {
                onClickSuperViewAllBenefitsCTA(subtitle?.text || "-", subtitle?.link || "-", undefined, {
                    planId: plan_details?.plan_id,
                    planPrice: priceInRupee(plan_details?.final_price),
                    planName: plan_details?.name,
                });
                navigateToDeeplinkGenericHelper(subtitle?.link || "-");
            };

            subscriptionLineItems.push({
                icon: logo_ctx?.logo,
                onLoad: onLoadCallback,
                title: {
                    value: title,
                },
                subtitle: {
                    value: subtitle?.text,
                    onClick: subtitle?.link ? handleSubtitleClick : undefined,
                    icon: subtitle?.text ? true : false,
                },
                rightSection: {
                    type: SavingsRightSectionType.BUTTON,
                    value: oneTapButton?.text,
                    onClick: isAddPlan
                        ? addSuperCallback
                        : () => handleRemove(widgetData?.meta as RenderingDetailsMeta),
                    buttonType: isAddPlan ? SavingsLineItemButtonType.PRIMARY : SavingsLineItemButtonType.SECONDARY,
                    color: isSwiggyBlackPlanVariants(plan_details)
                        ? theme.colors?.[theme.colorMode].primary_One_Blck
                        : "",
                },
                bottomText,
                bottomSheetProps: isAddPlan ? undefined : bottomSheetProps,
                showRemovePlanConfirmation: isAddPlan ? undefined : allowPlanRemovalBottomsheet,
                planDetails: plan_details,
            });
        } else if (isActiveUserUi) {
            const { benefits, logo_ctx, secondary_subtitles, identifiers, active_plan_details } =
                widgetData?.meta || {};
            const bottomText = pickSubtitles(secondary_subtitles, identifiers);
            subscriptionLineItems.push({
                icon: logo_ctx?.logo,
                onLoad: () =>
                    superOnLoad({
                        benefits: benefits?.[0],
                        totalSavings: bottomText || "",
                        onImpressionSwiggyOneSavingsNudge,
                    }),
                title: {
                    value: benefits?.[0]?.title,
                },
                subtitle: {
                    value: benefits?.[0]?.sub_title,
                },
                rightSection: {
                    type: SavingsRightSectionType.LABEL,
                    value: APPLIED,
                },
                bottomText,
                planDetails: active_plan_details,
            });
        }

        return subscriptionLineItems;
    }, [
        addSuperToCart,
        allowPlanRemovalBottomsheet,
        bottomSheetProps,
        clickEvent,
        handleRemove,
        isActiveUserUi,
        isBenefitsOneTapUi,
        isNudgeUi,
        onClickSuperViewAllBenefitsCTA,
        onImpressionSuperOneTapAddNudge,
        onImpressionSuperViewAllBenefitsCTA,
        onImpressionSwiggyOneSavingsNudge,
        theme,
        userSuperStatus,
        widgetData?.display_text,
        widgetData?.meta,
    ]);

    return { getSubscriptionSavingsLineItems, removePlanMessage };
};

export default useSubscriptionSavings;
